import React, { useState, useRef } from 'react';
import { Typography, withStyles, Tooltip, Link } from '@material-ui/core';
import {
  Column,
  Selection,
  Sorting
} from 'devextreme-react/data-grid';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { Tooltip as DevExTooltip } from 'devextreme-react/tooltip';
import columnSchema from 'components/underwrite/postAcquisition/postAcqUnderwrite/histories/taskHistoryColumnSchema';
import PostAcqViewUnderwriteModal from 'components/underwrite/postAcquisition/postAcqUnderwrite/histories/PostAcqViewUnderwriteModal';
import { on } from 'devextreme/events';
import { format } from 'date-fns';
import DataGrid from 'components/devExtreme/DataGrid';
import { Constants } from 'utilities/constants';
import classNames from 'classnames';
import { setPostAcqViewUnderwriteModalIsOpen, fetchPostAcqUnderwriteModalData } from 'store/actions';
import { sendClientLog } from 'services/logService';

const styles = theme => ({
  root: {
    minHeight: 204 // Keep consistent height
  },
  title: {
    marginBottom: 6,
  },
  grid: {
    maxHeight: 198 // Keep consistent height
  },
  link: {
    background: 'none',
    border: 'none',
    color: 'blue',
    textDecoration: 'underline',
    cursor: 'pointer',
    font: 'inherit',
    padding: 0,
    '&:hover': {
      color: 'darkblue'
    }
  },
  disabledLink: {
    cursor: 'default',
    '&:hover': {
      textDecoration: 'none'
    }
  }
});

let newTaskId = null;
let newLoadId = null;
let rowShaded = false;

const PostAcqUWTaskHistoryGrid = ({ classes, tasks, taskId, viewUnderwriteModalIsOpen, setPostAcqViewUnderwriteModalIsOpen,
  fetchPostAcqUnderwriteModalData
 }) => {
  const [cellTooltip, setCellTooltip] = useState(null);
  const cellTooltipRef = useRef(null);

  const onViewLinkClick = (row) => { // Renamed for clarity
    const { taskId, loadId, valuationId } = row;
    setPostAcqViewUnderwriteModalIsOpen(true);
    fetchPostAcqUnderwriteModalData(taskId, loadId, valuationId);
    sendClientLog('Info', `Post-Acq UW - User clicked View on task history grid. TaskId ${taskId}, loadId ${loadId}, valuationId ${valuationId}`);
  };

  // Custom cell render function for the "View" column
  const renderViewLink = (cellInfo) => {
    if ((cellInfo.data.taskId === taskId && cellInfo.data.isLatestLoad) || cellInfo.data.valuationId == null) return null;

    const isCurrentValuation = cellInfo.data.id === taskId;
    return (
      <Tooltip title={`View ${cellInfo.data.uwUser}'s underwrite`}>
      <Link
        component="button" // Treat as a button for accessibility/focus
        variant="body2" // Use a typography variant for text styling
        className={classNames(isCurrentValuation && classes.disabledLink)} // Apply custom link styles
        onClick={() => onViewLinkClick(cellInfo.data)} // Pass the row data
        disabled={isCurrentValuation || cellInfo.data.loadId == null}
        color={isCurrentValuation ? 'initial' : 'primary'}
        style={{ fontSize: 'inherit', lineHeight: 'inherit', verticalAlign: 'initial' }}

      >
        View
      </Link>
      </Tooltip>
    );
  };

  const onViewTaskClick = (id) => {
    const url = `${window.location.origin}${Constants.Routes.POSTACQ_TASK}/${id}`;
    window.open(url, '_blank');
  };

  // Custom cell render function for the "Task ID" column
  const renderTaskLink = (cellInfo) => {
    const isCurrentTask = cellInfo.value === taskId;
    if (isCurrentTask) {
      return (
        <span>
          {cellInfo.text + ' ⬅️'}
        </span>
      )
    }
    if (cellInfo.data.loadId == null) {
      return (
        <span>
          {cellInfo.text}
        </span>
      )
    }
    return (
      <Tooltip title='Open task in new tab'>
        <Link
          component="button" // Treat as a button for accessibility/focus
          variant="body2" // Use a typography variant for text styling
        onClick={() => onViewTaskClick(cellInfo.value)} // Pass the row data
        color={'primary'}
        style={{ fontSize: 'inherit', lineHeight: 'inherit', verticalAlign: 'initial' }}
        >
          {cellInfo.text}
        </Link>
      </Tooltip>
    );
  };

  const renderLoadIdLink = (cellInfo) => {
    if (cellInfo.value == null
        || cellInfo.data.valuationId != null
        || (cellInfo.data.taskId === taskId && cellInfo.data.isLatestLoad)) {
      return <span>{cellInfo.text}</span>;
    }
    return (
      <Tooltip title='View load'>
        <Link
          component="button"
          variant="body2"
        onClick={() => onViewLinkClick(cellInfo.data)}
        color={'primary'}
        style={{ fontSize: 'inherit', lineHeight: 'inherit', verticalAlign: 'initial' }}
        >
          {cellInfo.text}
        </Link>
      </Tooltip>
    );
  };

  const renderValuationNotesCell = (cellInfo) => {
    const text = cellInfo.value || '';  
  
    const ellipsisStyle = {
      whiteSpace:    'nowrap',
      overflow:      'hidden',
      textOverflow:  'ellipsis',
      display:       'block',
      width:         '100%',
      cursor:        'help'
    };
  
    return (
      <Tooltip title={text}>
        <span style={ellipsisStyle}>
          {text}
        </span>
      </Tooltip>
    );
  };

  const renderTypeCell = (cellInfo) => {
    const { channel, stage, requestType } = cellInfo.data;
    const text = requestType ?? '';
    const tooltip = requestType ? `${channel ?? ''} - ${stage ?? ''} - ${requestType}` : '';

    if (!text) return null;

    return (
      <Tooltip title={tooltip}>
        <span>{text}</span>
      </Tooltip>
    );
  };

  const calculateTypeSortValue = (rowData) => {
    return rowData.requestType || '';
  };

  const renderValuationUserCell = (cellInfo) => {
    // const isDotted = cellInfo.data.id === 0;
    // const isRed = cellInfo.data.valuation?.valuationOutput?.isSuccess === false;
    const cellStyle = {};
    // if (isDotted) {
    //   cellStyle.outline = '1px dotted gray';
    //   cellStyle.padding = '0 2px';
    //   cellStyle.display = 'inline-block';
    // }
    // if (isRed) {
    //   cellStyle.color = 'red';
    // }
    return (
      <span style={cellStyle}>
        {cellInfo.value}
      </span>
    );
  };

  const onRowPrepared = e => {
    if (e.rowType === "data") {
      if (e.data.loadId !== newLoadId) {
        newLoadId = e.data.loadId;
        rowShaded = !rowShaded;
        if (e.rowIndex !== 0) e.rowElement.style.borderTop = '2px solid #b9b9b9';
      }
      if (e.data.taskId !== newTaskId) {
        newTaskId = e.data.taskId;
        rowShaded = !rowShaded;
        if (e.rowIndex !== 0) e.rowElement.style.borderTop = '2px solid #000000';
      }
      if (!e.data.isSelectedValuation) {
        e.rowElement.style.opacity = 0.5;
      }
    }
  }

  const onCellPrepared = e => {
    if (e.rowType === 'data' && e.column.dataType === 'date' && e.value) {
      on(e.cellElement, 'mouseover', async(mouseEvent) => {
        setCellTooltip(format(e.value, 'MM/dd/yyyy, h:mm:ss a'));
        await cellTooltipRef.current?.instance.show(mouseEvent.target);
      });

      on(e.cellElement, 'mouseout', async() => {
        await cellTooltipRef.current?.instance.hide();
      });
    }
  }

  return (
    <div className={classes.root}>
      {
        tasks && tasks?.length > 0 ?
          <>
            <DataGrid
              id={'taskHistoryGrid'}
              dataSource={tasks}
              showBorders={true}
              showRowLines={true}
              showColumnLines={true}
              hoverStateEnabled={true}
              allowColumnResizing={true}
              className={classes.grid}
              columnAutoWidth={true}
              onCellPrepared={onCellPrepared}
              width='100%'
              height={198} // Keeping a fixed height, adjust if needed
              onRowPrepared={onRowPrepared}
              enableTooltips
              tooltips={columnSchema}
            >
              <Sorting mode={'multiple'} />
              <Selection mode='single' />
              <Column
                caption=""
                cellRender={renderViewLink} // Use the link renderer
                width={60}
                allowSorting={false}
                allowFiltering={false}
                fixed={true}
              />
              {columnSchema.map(({ field, displayName, width, type, format, sortIndex, sortOrder }) => {
                return <Column
                  key={field}
                  dataField={field}
                  caption={displayName}
                  width={width}
                  dataType={type}
                  format={format}
                  sortIndex={sortIndex}
                  sortOrder={sortOrder}
                  cellRender={field === 'uwNotes' ? renderValuationNotesCell :
                                field === 'uwUser' ? renderValuationUserCell :
                                field === 'taskId' ? renderTaskLink :
                                field === 'loadId' ? renderLoadIdLink :
                                field === 'requestType' ? renderTypeCell : null}
                  calculateSortValue={field === 'requestType' ? calculateTypeSortValue : undefined}
                  showEditorAlways={false}
                  trueText={'✔'}
                  falseText={'✖'}
                />
              })}
            </DataGrid>
            <DevExTooltip
              ref={cellTooltipRef}
              position="top"
            >
              <p>{cellTooltip}</p>
            </DevExTooltip>
          </>
          :
          <Typography className={classes.title} variant="subtitle2">
            No valuations
          </Typography>
      }
      <PostAcqViewUnderwriteModal
        open={viewUnderwriteModalIsOpen}
        handleClose={() => setPostAcqViewUnderwriteModalIsOpen(false)}
        viewingReadonlyMode={viewUnderwriteModalIsOpen}
      />
    </div>
  );
};

const mapStateToProps = ({ appState, postAcqUWState }) => {
  return {
    viewUnderwriteModalIsOpen: postAcqUWState.viewUnderwriteModalIsOpen
  }
};

const mapDispatchToProps = {
  setPostAcqViewUnderwriteModalIsOpen,
  fetchPostAcqUnderwriteModalData
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(PostAcqUWTaskHistoryGrid);
